/**
 * PerfStatsPanel — admin-only performance telemetry, opened from the
 * "Performance" button next to Users on the dashboard.
 *
 * One row per user (worst experience first): GPU, sessions, hours, average and
 * 5th-percentile FPS, % of time under 50fps. Clicking a row expands their
 * recent sessions so a report like "it runs at 30fps with a tiny chart" can be
 * matched to the exact machine (GPU / resolution / dpr), editor, perf mode and
 * chart size it happened on. Data comes from perf/perfTelemetry.ts capture.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import {
  perfSummary, perfUserSessions, type PerfSessionDoc, type PerfUserRow,
} from '../../api/telemetryClient';
import { color, font, ui } from '../ui/theme';
import { sectionStyle, resetBtnStyle, hintTextStyle } from './SettingsDialog.styles';

interface Props { open: boolean; onClose: () => void }

/** 55+ green, 40+ amber, else red — same thresholds as the editor FPS pill. */
function fpsColor(v: number | null | undefined): string {
  if (v == null) return color.inkFaint;
  return v >= 55 ? '#34D399' : v >= 40 ? '#FBBF24' : '#F87171';
}

function fmtAgo(ms: number): string {
  if (!ms) return '—';
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 90) return 'just now';
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 129600) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function fmtHours(seconds: number): string {
  if (seconds < 90) return `${seconds}s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

/** "NVIDIA GeForce GTX 1080/PCIe/SSE2 (ANGLE …)" → "GTX 1080". Best effort. */
function shortGpu(gpu: string): string {
  const m = gpu.match(/(RTX\s*\d{3,4}\s*(Ti|SUPER)?|GTX\s*\d{3,4}\s*(Ti)?|Radeon[^,(/]*|Iris[^,(/]*|UHD Graphics\s*\d*|HD Graphics\s*\d*|Apple M\d\w*|Adreno[^,(/]*|Mali[^,(/]*)/i);
  return (m ? m[1] : gpu).trim().replace(/\s+/g, ' ').slice(0, 28) || 'unknown';
}

/** CPU-bound vs GPU-bound verdict from a session's timing block. */
function boundVerdict(t: PerfSessionDoc['stats']['timing']): { label: string; color: string } | null {
  if (!t) return null;
  const gpu = t.gpu_med_ms ?? null;
  const cpu = t.cpu_render_avg_ms ?? null;
  if (gpu != null && gpu > 10 && (cpu == null || gpu > cpu * 1.5)) return { label: 'GPU-bound', color: '#F87171' };
  if (cpu != null && cpu > 8 && (gpu == null || cpu > gpu * 1.5)) return { label: 'CPU-bound', color: '#FBBF24' };
  if (gpu != null || cpu != null) return { label: 'balanced', color: color.inkFaint };
  return null;
}

export default function PerfStatsPanel({ open, onClose }: Props) {
  const isAdmin = useAuthStore(s => s.mapper?.role === 'admin');
  const [rows, setRows] = useState<PerfUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sessions, setSessions] = useState<Record<string, PerfSessionDoc[]>>({});

  const refresh = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const body = await perfSummary();
      setRows(body.users);
    } catch (e: any) {
      setErr(e?.message || 'Failed to load telemetry.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open && isAdmin) refresh(); }, [open, isAdmin, refresh]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const toggleRow = async (email: string) => {
    if (expanded === email) { setExpanded(null); return; }
    setExpanded(email);
    if (!sessions[email]) {
      try {
        const body = await perfUserSessions(email);
        setSessions(prev => ({ ...prev, [email]: body.sessions }));
      } catch { /* row stays without detail; the summary already shows the headline */ }
    }
  };

  if (!open || !isAdmin) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={dialogStyle} onClick={e => e.stopPropagation()}>
        <div style={headerStyle}>
          <span style={{ fontWeight: 600, fontSize: 14, color: color.ink }}>Performance</span>
          <button style={{ ...ui.closeBtn }} onClick={onClose}>{'✕'}</button>
        </div>
        <div style={{ ...sectionStyle, overflowY: 'auto' }}>
          <div style={hintTextStyle}>
            Framerate telemetry from every editor session, worst experience first.
            Click a user to see their recent sessions.
          </div>

          {err && <div style={errStyle}>{err}</div>}
          {loading ? (
            <div style={{ ...hintTextStyle, marginTop: 10 }}>Loading…</div>
          ) : (
            <div>
              <div style={gridHeaderStyle}>
                <span>User / GPU</span>
                <span style={num}>Avg</span>
                <span style={num}>P5</span>
                <span style={num}>&lt;50fps</span>
                <span style={num}>Time</span>
                <span style={num}>Seen</span>
              </div>
              {rows.map(r => (
                <div key={r.email}>
                  <div style={rowStyle} onClick={() => toggleRow(r.email)} title={r.gpu}>
                    <span style={{ minWidth: 0 }}>
                      <div style={emailStyle}>
                        {r.email}
                        {/(swiftshader|microsoft basic|llvmpipe|angle \(microsoft, microsoft)/i.test(r.gpu) && (
                          <span style={softwareTag} title={`WebGL is software-rendered on this machine (no GPU in use): ${r.gpu}. Fix: enable the browser's hardware acceleration.`}>
                            SOFTWARE GL
                          </span>
                        )}
                      </div>
                      <div style={gpuStyle} title={r.gpu}>
                        {shortGpu(r.gpu)}
                        {r.screen ? ` · ${r.screen[0]}×${r.screen[1]}@${(Math.round((r.device_pixel_ratio ?? 1) * 100) / 100)}x` : ''}
                        {r.cores ? ` · ${r.cores}c` : ''}
                        {r.memory_gb ? `/${r.memory_gb}GB` : ''}
                      </div>
                      {/* The two facts that decide whether a slow report is
                          OUR problem: which mode they sat in, and which build
                          they ran. A slow user parked in Quality on a stale
                          build isn't a regression. */}
                      <div style={gpuStyle}>
                        {r.dominant_mode && (
                          <span style={modeChip(r.dominant_mode, r.fps_avg)}>{r.dominant_mode}</span>
                        )}
                        {r.build ? <span style={{ marginLeft: 6 }}>{r.build}</span> : <span style={{ marginLeft: 6, opacity: 0.6 }}>build ?</span>}
                        {(() => { const v = boundVerdict(r.worst_timing); return v && (
                          <span style={{ marginLeft: 6, color: v.color }}>{v.label}</span>
                        ); })()}
                      </div>
                    </span>
                    <span style={{ ...num, color: fpsColor(r.fps_avg), fontWeight: 700 }}>{r.fps_avg ?? '—'}</span>
                    <span style={{ ...num, color: fpsColor(r.fps_p5), fontWeight: 700 }}>{r.fps_p5 ?? '—'}</span>
                    <span style={{ ...num, color: (r.below50_pct ?? 0) > 25 ? '#F87171' : color.inkDim }}>
                      {r.below50_pct != null ? `${r.below50_pct}%` : '—'}
                    </span>
                    <span style={{ ...num, color: color.inkDim }}>{fmtHours(r.seconds)}</span>
                    <span style={{ ...num, color: color.inkFaint }}>{fmtAgo(r.last_seen)}</span>
                  </div>

                  {expanded === r.email && (
                    <div style={detailBoxStyle}>
                      {(sessions[r.email] ?? []).map(s => (
                        <div key={s.session_id} style={sessionRowStyle}>
                          <span style={{ color: color.inkDim, flex: '0 0 92px' }}>
                            {new Date(s.started_at).toLocaleDateString()}{' '}
                            {new Date(s.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span style={{ flex: '0 0 66px', color: color.accentSoft }}>{s.editor}</span>
                          {s.device.build && (
                            <span style={{ ...numI, color: color.inkFaint }} title="Editor build this session ran">
                              {s.device.build}
                            </span>
                          )}
                          <span style={{ ...numI, color: fpsColor(s.stats.fps?.avg) }}>
                            {s.stats.fps?.avg ?? '—'} avg
                          </span>
                          <span style={{ ...numI, color: fpsColor(s.stats.fps?.p5) }}>
                            {s.stats.fps?.p5 ?? '—'} p5
                          </span>
                          <span style={numI}>{s.stats.chart_notes ?? 0} notes</span>
                          <span style={numI}>
                            {s.stats.render?.canvas ? `${s.stats.render.canvas[0]}×${s.stats.render.canvas[1]}` : '—'}
                          </span>
                          <span style={numI}>
                            {s.stats.render?.scale_min != null && s.stats.render.scale_min < 1
                              ? `↓res ${Math.round((s.stats.render.scale_min) * 100)}%` : 'native'}
                          </span>
                          {(() => { const v = boundVerdict(s.stats.timing); return v && (
                            <span style={{ ...numI, color: v.color }} title={
                              `cpu ${s.stats.timing?.cpu_render_avg_ms ?? '—'}ms · gpu ${s.stats.timing?.gpu_med_ms ?? '—'}ms (p95 ${s.stats.timing?.gpu_p95_ms ?? '—'})`
                            }>{v.label}</span>
                          ); })()}
                          {s.stats.memory?.js_heap_max_mb != null && (
                            <span style={numI} title={`heap limit ${s.stats.memory.js_heap_limit_mb ?? '—'}MB · ${s.stats.memory.textures_max ?? 0} tex / ${s.stats.memory.geometries_max ?? 0} geo`}>
                              {s.stats.memory.js_heap_max_mb}MB
                            </span>
                          )}
                          {((s.stats.timing?.hitch_100ms ?? 0) + (s.stats.timing?.hitch_250ms ?? 0)) > 0 && (
                            <span style={{ ...numI, color: '#FBBF24' }} title={`longest frame ${s.stats.timing?.longest_frame_ms ?? '—'}ms`}>
                              {(s.stats.timing!.hitch_100ms ?? 0) + (s.stats.timing!.hitch_250ms ?? 0)} hitch
                            </span>
                          )}
                          <span style={{ ...numI, color: color.inkFaint }}>{fmtHours(s.stats.seconds ?? 0)}</span>
                        </div>
                      ))}
                      {(() => {
                        const ws = (sessions[r.email] ?? []).flatMap(s => (s.stats.worst_seconds ?? []).map(w => ({ ...w, ed: s.editor })));
                        ws.sort((a, b) => a.fps - b.fps);
                        return ws.length > 0 && (
                          <div style={worstNoteStyle}>
                            Worst seconds: {ws.slice(0, 3).map(w =>
                              `${w.fps}fps @ beat ${w.beat} (${w.ed}, ${w.calls} calls, ${w.notes} notes${w.playing ? ', playing' : ''})`,
                            ).join(' · ')}
                          </div>
                        );
                      })()}
                      {!(sessions[r.email]?.length) && (
                        <div style={{ ...hintTextStyle, padding: 6 }}>Loading sessions…</div>
                      )}
                      {r.worst_session?.fps && (
                        <div style={worstNoteStyle}>
                          Worst session: {r.worst_session.editor} — p5 {r.worst_session.fps.p5}fps,
                          min {r.worst_session.fps.min}fps, {r.worst_session.chart_notes ?? 0} notes
                          {r.worst_session.render?.canvas ? `, ${r.worst_session.render.canvas[0]}×${r.worst_session.render.canvas[1]}` : ''}.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {rows.length === 0 && (
                <div style={{ ...hintTextStyle, marginTop: 8 }}>
                  No telemetry yet — it starts arriving as soon as anyone opens an editor on this build.
                </div>
              )}
            </div>
          )}

          <button
            style={{ ...resetBtnStyle, color: color.accentSoft, background: 'transparent', borderColor: 'var(--line)', marginTop: 8 }}
            onClick={refresh}
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = { ...ui.overlay };
const dialogStyle: React.CSSProperties = {
  ...ui.dialog, width: '94%', maxWidth: 760, maxHeight: '80vh',
  display: 'flex', flexDirection: 'column',
};
const headerStyle: React.CSSProperties = { ...ui.dialogHeader, padding: '12px 16px' };

const GRID = 'minmax(180px, 1fr) 52px 52px 62px 56px 72px';

const gridHeaderStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: GRID, gap: 6, padding: '4px 8px',
  fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
  color: color.accentSoft, fontFamily: font.sans,
};

const rowStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: GRID, gap: 6, padding: '8px 8px',
  borderBottom: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.08)',
  fontFamily: font.sans, cursor: 'pointer', alignItems: 'center',
};

const emailStyle: React.CSSProperties = {
  fontSize: 12, color: color.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};

/** Mode chip: amber when a struggling user is parked in Quality (the mode
 *  that can't help them), quiet otherwise. */
const modeChip = (mode: string, avg: number | null): React.CSSProperties => {
  const bad = mode === 'quality' && (avg ?? 60) < 50;
  return {
    padding: '1px 5px', borderRadius: 4, fontSize: 8.5, fontWeight: 700,
    letterSpacing: 0.4, textTransform: 'uppercase',
    color: bad ? '#FBBF24' : color.inkFaint,
    background: bad ? 'rgba(251,191,36,0.12)' : 'oklch(0.4 0.02 280 / 0.25)',
    border: `1px solid ${bad ? 'rgba(251,191,36,0.4)' : 'var(--line)'}`,
  };
};

const softwareTag: React.CSSProperties = {
  marginLeft: 6, padding: '1px 6px', borderRadius: 5,
  fontSize: 8.5, fontWeight: 800, letterSpacing: 0.6, verticalAlign: 'middle',
  color: '#FCA5A5', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.45)',
};

const gpuStyle: React.CSSProperties = {
  fontSize: 10, color: color.inkFaint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};

const num: React.CSSProperties = {
  fontSize: 11, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
};

const numI: React.CSSProperties = { ...num, textAlign: 'left', flex: '0 0 auto', color: color.inkDim };

const detailBoxStyle: React.CSSProperties = {
  margin: '2px 4px 8px 4px', padding: '6px 8px', borderRadius: 8,
  background: 'oklch(0.16 calc(0.02 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
  display: 'flex', flexDirection: 'column', gap: 3,
};

const sessionRowStyle: React.CSSProperties = {
  display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 10.5,
  fontFamily: font.sans, flexWrap: 'wrap',
};

const worstNoteStyle: React.CSSProperties = {
  marginTop: 4, fontSize: 10, color: '#FBBF24', fontFamily: font.sans,
};

const errStyle: React.CSSProperties = {
  fontSize: 11, color: '#fca5a5', marginTop: 6,
  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
  borderRadius: 8, padding: '8px 10px', fontFamily: font.sans,
};
